// T021: Create QuickPostImagePreview component

import { X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface QuickPostImagePreviewProps {
  /** Local blob URL for preview */
  previewUrl: string
  /** Upload progress percentage (0-100) */
  uploadProgress?: number
  /** Whether image is currently uploading */
  isUploading?: boolean
  /** Callback when remove button is clicked */
  onRemove: () => void
  /** Whether the component is disabled */
  disabled?: boolean
}

export function QuickPostImagePreview({
  previewUrl,
  uploadProgress = 0,
  isUploading = false,
  onRemove,
  disabled = false,
}: QuickPostImagePreviewProps) {
  return (
    <div className="relative mt-3">
      {/* Image container */}
      <div className="relative overflow-hidden rounded-lg">
        <img
          src={previewUrl}
          alt="Upload preview"
          className="max-h-64 w-full object-cover"
        />

        {/* Upload progress overlay */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="flex flex-col items-center gap-2 text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm font-medium">{uploadProgress}%</span>
            </div>
          </div>
        )}

        {/* Progress bar */}
        {isUploading && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Remove button */}
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className="absolute right-2 top-2 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80"
        onClick={onRemove}
        disabled={disabled || isUploading}
        aria-label="Remove image"
      >
        <X className="h-4 w-4 text-white" />
      </Button>
    </div>
  )
}
